"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { ArrowRight } from "lucide-react";
const steps = [{
  id: 1,
  title: "Inspect\xA0",
  description: "Our FDACS licensed experts perform a comprehensive evaluation of your property to identify pest entry points and nesting sites."
}, {
  id: 2,
  title: "Treat",
  description: "We apply targeted, professional-grade solutions to eliminate active infestations and create a protective barrier around your home."
}, {
  id: 3,
  title: "Monitor",
  description: "Regular follow-ups ensure your home remains pest-free, with adjustments made based on seasonal pest activity in Florida."
}, {
  id: 4,
  title: "Protect",
  description: "Ongoing maintenance and preventative measures keep your family safe from termites, mosquitoes, and spiders year-round."
}];
export default function TheRoadToClosing() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="223476" id="our-treatment-process" className="bg-background py-24 px-6 md:px-12 overflow-hidden" ref={ref}>
      <div className={`max-w-6xl mx-auto transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
        <div className="text-center mb-16 md:mb-24">
          <h2 className="text-3xl md:text-5xl font-default font-bold text-primary mb-6">
            Why <Link to="/PuntaGordaPestControlPage" className="hover:text-secondary transition-colors">Punta Gorda</Link> Residents Trust Peace River
          </h2>
          <p className="text-lg text-primary max-w-2xl mx-auto font-serif mb-4">We follow a rigorous, proven approach to ensure your home in <Link to="/PuntaGordaPestControlPage" className="underline decoration-2 underline-offset-4 hover:text-secondary transition-colors">Punta Gorda</Link> stays protected from invasive pests and structural damage.</p>
          <p className="text-base text-primary/80 max-w-3xl mx-auto font-serif">
            Our process in <Link to="/PortCharlottePestControlPage" className="underline decoration-1 underline-offset-2 hover:text-secondary transition-colors">Port Charlotte</Link> ensures optimal results, we are also <Link to="/CapeCoralPestControlPage" className="underline decoration-1 underline-offset-2 hover:text-secondary transition-colors">serving Cape Coral</Link>, and our services are available in <Link to="/NorthFortMyersPestControlPage" className="underline decoration-1 underline-offset-2 hover:text-secondary transition-colors">North Fort Myers</Link>.
          </p>
        </div>

        <div className="relative mb-20">
          {}
          <div className="absolute top-8 left-[10%] right-[10%] h-0.5 border-t-2 border-dashed border-primary z-0 hidden md:block" />

          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-12 md:gap-4 relative z-10">
            <TooltipProvider delayDuration={200}>
              {steps.map((step, i) => <div key={step.id} data-index={i} className="flex flex-col items-center relative w-full md:w-1/4 group">
                  {}
                  {i !== steps.length - 1 && <div className="absolute top-16 left-1/2 w-0.5 h-[calc(100%+1rem)] border-l-2 border-dashed border-primary -translate-x-1/2 z-0 md:hidden" />}

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="w-16 h-16 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-2xl font-bold font-default shadow-none cursor-help z-10 relative transition-transform duration-300 group-hover:scale-110 group-hover:bg-secondary group-hover:text-secondary-foreground">
                        {step.id}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[250px] text-center p-4 shadow-xl bg-card border-2 border-primary" sideOffset={10}>
                      <p className="font-bold font-default text-base mb-2 text-foreground">
                        {step.title}
                      </p>
                      <p className="text-sm font-serif text-foreground opacity-90 leading-relaxed">
                        {step.description}
                      </p>
                    </TooltipContent>
                  </Tooltip>

                  <div className="mt-6 text-center bg-background/80 backdrop-blur-sm px-4 py-2 rounded-none">
                    <h3 className="font-default font-bold text-xl text-primary mb-2">
                      {step.title}
                    </h3>
                    {}
                    <p className="text-sm text-primary font-serif md:hidden max-w-[250px] mx-auto">
                      {step.description}
                    </p>
                  </div>
                </div>)}
            </TooltipProvider>
          </div>
        </div>

        <div className="text-center">
          <Button asChild size="lg" className="rounded-full px-8 text-base shadow-none bg-primary text-primary-foreground hover:scale-105 transition-transform">
            <Link to="/ContactPage">
              Schedule Your Free Inspection
              <ArrowRight className="ml-2 size-5" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}